import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Shield, 
  Search, 
  Trash2, 
  Eye, 
  EyeOff,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Mail,
  Phone,
  Globe,
  Lock,
  Loader2,
  Crown,
  Copy,
  ExternalLink,
  Key,
  BookOpen,
  ChevronRight,
  Calendar,
  Bell,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PremiumServices() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('breach');
  const [loading, setLoading] = useState(false);
  const [breachResult, setBreachResult] = useState(null);
  const [passwordResult, setPasswordResult] = useState(null);
  const [removalStatus, setRemovalStatus] = useState(null);
  const [identityStatus, setIdentityStatus] = useState(null);
  const [emailToCheck, setEmailToCheck] = useState(user?.email || '');
  const [passwordToCheck, setPasswordToCheck] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [removalForm, setRemovalForm] = useState({ email: user?.email || '', full_name: '', phone: '', address: '' });
  
  // Opt-out progress state
  const [optoutProgress, setOptoutProgress] = useState([]);
  const [optoutStats, setOptoutStats] = useState({ total_submitted: 0, total_completed: 0, pending_reminders: 0 });
  const [savingOptout, setSavingOptout] = useState(null);

  useEffect(() => {
    if (user?.is_premium) {
      fetchRemovalStatus();
      fetchIdentityStatus();
      fetchOptoutProgress();
    }
  }, [user]);

  const fetchOptoutProgress = async () => {
    try {
      const response = await axios.get(`${API}/optout/progress`);
      setOptoutProgress(response.data.progress || []);
      setOptoutStats(response.data.stats || { total_submitted: 0, total_completed: 0, pending_reminders: 0 });
    } catch (error) {
      console.error('Failed to fetch opt-out progress');
    }
  };

  const toggleOptoutTracking = async (broker, category) => {
    const isTracked = optoutProgress.some(p => p.broker_name === broker.name);
    setSavingOptout(broker.name);
    
    try {
      if (isTracked) {
        // Remove from tracking
        await axios.delete(`${API}/optout/progress/${encodeURIComponent(broker.name)}`);
        setOptoutProgress(prev => prev.filter(p => p.broker_name !== broker.name));
        toast.success(`Removed ${broker.name} from tracking`);
      } else {
        // Add to tracking with 30-day reminder
        const reminderDate = new Date();
        reminderDate.setDate(reminderDate.getDate() + 30);
        
        await axios.post(`${API}/optout/progress`, {
          broker_name: broker.name,
          broker_url: broker.url,
          category: category,
          reminder_date: reminderDate.toISOString().split('T')[0]
        });
        
        // Refresh the list
        await fetchOptoutProgress();
        toast.success(`Tracking ${broker.name} - reminder set for 30 days`);
      }
    } catch (error) {
      toast.error('Failed to update tracking');
    } finally {
      setSavingOptout(null);
    }
  };

  const markOptoutCompleted = async (brokerName, completed) => {
    setSavingOptout(brokerName);
    try {
      await axios.put(`${API}/optout/progress/${encodeURIComponent(brokerName)}`, { completed });
      setOptoutProgress(prev => prev.map(p => 
        p.broker_name === brokerName ? { ...p, completed } : p
      ));
      toast.success(completed ? 'Marked as completed!' : 'Marked as pending');
      fetchOptoutProgress(); // Refresh stats
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setSavingOptout(null);
    }
  };

  const isOptoutTracked = (brokerName) => optoutProgress.some(p => p.broker_name === brokerName);
  const isOptoutCompleted = (brokerName) => optoutProgress.find(p => p.broker_name === brokerName)?.completed;

  const checkBreach = async () => {
    if (!emailToCheck) return;
    setLoading(true);
    setBreachResult(null); // Clear previous results
    try {
      const response = await axios.post(`${API}/premium/breach-check`, { email: emailToCheck });
      // Only set result if it's a valid breach check response
      if (response.data && response.data.email) {
        setBreachResult(response.data);
        toast.success('Breach check complete');
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Premium subscription required');
      } else {
        toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || 'Failed to check breaches');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPassword = async () => {
    if (!passwordToCheck) {
      toast.error('Please enter a password to check');
      return;
    }
    setLoading(true);
    setPasswordResult(null); // Clear previous results
    try {
      const response = await axios.post(`${API}/premium/password-check`, { password: passwordToCheck });
      // Only set result if it's a valid password check response
      if (response.data && typeof response.data.compromised !== 'undefined') {
        setPasswordResult(response.data);
        if (response.data.compromised) {
          toast.error(`Password found in ${response.data.breach_count.toLocaleString()} breaches!`);
        } else {
          toast.success('Password not found in any breaches!');
        }
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Premium subscription required');
      } else {
        toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || 'Failed to check password');
      }
    } finally {
      setLoading(false);
    }
  };

  const startDataRemoval = async () => {
    if (!removalForm.email || !removalForm.full_name) {
      toast.error('Please fill in required fields');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/premium/data-removal/start`, removalForm);
      toast.success(response.data.message);
      fetchRemovalStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start removal');
    } finally {
      setLoading(false);
    }
  };

  const fetchRemovalStatus = async () => {
    try {
      const response = await axios.get(`${API}/premium/data-removal/status`);
      setRemovalStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch removal status');
    }
  };

  const fetchIdentityStatus = async () => {
    try {
      const response = await axios.get(`${API}/premium/identity-monitor`);
      setIdentityStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch identity status');
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
  };

  if (!user?.is_premium) {
    return (
      <div className="min-h-screen bg-[#050505] py-16" data-testid="premium-upgrade-prompt">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center mb-6">
            <Crown className="w-10 h-10 text-[#00FF94]" />
          </div>
          <h1 className="font-heading font-bold text-3xl text-white mb-4">
            Premium Services
          </h1>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            Unlock advanced protection features including breach monitoring, automated data removal, and real-time identity protection.
          </p>
          
          <div className="grid gap-4 mb-8 text-left">
            {[
              { icon: Search, title: 'Dark Web Monitoring', desc: 'Continuous scanning for your exposed credentials' },
              { icon: Trash2, title: 'Data Broker Removal', desc: 'Automated removal from 100+ data brokers' },
              { icon: Eye, title: 'Identity Monitoring', desc: 'Real-time alerts for new exposures' },
              { icon: FileText, title: 'Removal Letters', desc: 'Auto-generated legal removal requests' },
            ].map((feature, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-[#0a0a0a] border border-[#262626] rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-[#00FF94]/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-[#00FF94]" />
                </div>
                <div>
                  <h3 className="font-medium text-white">{feature.title}</h3>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <Link to="/pricing">
            <Button className="btn-primary px-8" data-testid="upgrade-premium-btn">
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Premium
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="premium-services-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
            <Crown className="w-6 h-6 text-[#00FF94]" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl text-white">
              Premium Services
            </h1>
            <p className="text-gray-500">Advanced privacy protection tools</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#0a0a0a] border border-[#262626] p-1 grid grid-cols-5 w-full max-w-3xl">
            <TabsTrigger value="breach" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <Search className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Email </span>Breach
            </TabsTrigger>
            <TabsTrigger value="password" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <Key className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Password </span>Check
            </TabsTrigger>
            <TabsTrigger value="removal" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <Trash2 className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Data </span>Removal
            </TabsTrigger>
            <TabsTrigger value="identity" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <Eye className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Identity </span>Monitor
            </TabsTrigger>
            <TabsTrigger value="optout" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black text-xs sm:text-sm">
              <BookOpen className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Opt-Out </span>Guide
            </TabsTrigger>
          </TabsList>

          {/* Breach Check Tab */}
          <TabsContent value="breach" className="space-y-6">
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-[#00FF94]" />
                  Dark Web & Breach Scanner
                </CardTitle>
                <CardDescription className="text-gray-500">
                  Check if your email has been exposed in data breaches
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <Input
                    type="email"
                    value={emailToCheck}
                    onChange={(e) => setEmailToCheck(e.target.value)}
                    placeholder="Enter email to check"
                    className="flex-1 bg-[#171717] border-[#262626] text-white"
                    data-testid="breach-email-input"
                  />
                  <Button 
                    onClick={checkBreach} 
                    disabled={loading}
                    className="btn-primary"
                    data-testid="check-breach-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Scan'}
                  </Button>
                </div>

                {breachResult && (
                  <div className="mt-6 space-y-4">
                    {/* Risk Level */}
                    <div className="flex items-center justify-between p-4 bg-[#171717] rounded-lg border border-[#262626]">
                      <div>
                        <p className="text-sm text-gray-500">Risk Level</p>
                        <p className="text-xl font-bold text-white capitalize">{breachResult.risk_level}</p>
                      </div>
                      <Badge className={`text-lg px-4 py-2 ${getRiskColor(breachResult.risk_level)}`}>
                        {breachResult.breaches_found} Breaches Found
                      </Badge>
                    </div>

                    {/* Dark Web Scan Results */}
                    <div className="p-4 bg-[#171717] rounded-lg border border-[#262626]">
                      <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                        <Globe className="w-4 h-4 text-red-400" />
                        Dark Web Scan Results
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Exposures Found</p>
                          <p className="text-lg font-mono text-white">{breachResult.dark_web_scan?.exposures_found || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Data Types Exposed</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {breachResult.dark_web_scan?.data_types_exposed?.map((type, i) => (
                              <Badge key={i} className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">
                                {type}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Breaches List */}
                    {breachResult.breaches?.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-white">Breaches Detected:</h4>
                        {breachResult.breaches.map((breach, i) => (
                          <div key={i} className="p-4 bg-[#171717] rounded-lg border border-red-500/20">
                            <div className="flex justify-between items-start">
                              <div>
                                <h5 className="font-medium text-white">{breach.name}</h5>
                                <p className="text-xs text-gray-500">Breach date: {breach.date}</p>
                              </div>
                              <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                                {(breach.records / 1000000).toFixed(0)}M records
                              </Badge>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {breach.compromised_data.map((data, j) => (
                                <span key={j} className="text-xs px-2 py-0.5 bg-[#262626] rounded text-gray-400">
                                  {data}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recommendations */}
                    <div className="p-4 bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg">
                      <h4 className="font-medium text-[#00FF94] mb-2">Recommendations</h4>
                      <ul className="space-y-1">
                        {breachResult.recommendations?.map((rec, i) => (
                          <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-[#00FF94] flex-shrink-0 mt-0.5" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Password Check Tab - REAL HIBP Integration */}
          <TabsContent value="password" className="space-y-6">
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-[#00FF94]" />
                  Password Breach Checker
                  <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs ml-2">
                    REAL API
                  </Badge>
                </CardTitle>
                <CardDescription className="text-gray-500">
                  Check if your password has been exposed in data breaches using the <strong>Have I Been Pwned</strong> database.
                  <br />
                  <span className="text-xs text-gray-600">
                    Your password is never sent to any server - we use a secure k-Anonymity model.
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={passwordToCheck}
                      onChange={(e) => setPasswordToCheck(e.target.value)}
                      placeholder="Enter password to check"
                      className="flex-1 bg-[#171717] border-[#262626] text-white pr-10"
                      data-testid="password-check-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button 
                    onClick={checkPassword} 
                    disabled={loading || !passwordToCheck}
                    className="btn-primary"
                    data-testid="check-password-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
                  </Button>
                </div>

                <div className="p-3 bg-[#171717] border border-[#262626] rounded-lg">
                  <p className="text-xs text-gray-500 flex items-start gap-2">
                    <Lock className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#00FF94]" />
                    <span>
                      <strong className="text-gray-400">How it works:</strong> Only the first 5 characters of your password's SHA-1 hash are sent to the API. 
                      Your actual password never leaves your browser. This is the same secure method used by 1Password, Bitwarden, and other major password managers.
                    </span>
                  </p>
                </div>

                {passwordResult && (
                  <div className="mt-6 space-y-4">
                    {/* Result Banner */}
                    <div className={`p-4 rounded-lg border ${
                      passwordResult.compromised 
                        ? 'bg-red-500/10 border-red-500/30' 
                        : 'bg-green-500/10 border-green-500/30'
                    }`}>
                      <div className="flex items-center gap-3">
                        {passwordResult.compromised ? (
                          <AlertTriangle className="w-8 h-8 text-red-400" />
                        ) : (
                          <CheckCircle2 className="w-8 h-8 text-green-400" />
                        )}
                        <div>
                          <p className={`text-lg font-bold ${passwordResult.compromised ? 'text-red-400' : 'text-green-400'}`}>
                            {passwordResult.compromised ? 'Password Compromised!' : 'Password Safe'}
                          </p>
                          <p className="text-sm text-gray-400">{passwordResult.message}</p>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    {passwordResult.compromised && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-[#171717] rounded-lg text-center">
                          <p className="text-2xl font-mono text-red-400">{passwordResult.breach_count.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">Times Found in Breaches</p>
                        </div>
                        <div className="p-4 bg-[#171717] rounded-lg text-center">
                          <Badge className={`text-lg px-4 py-2 ${getRiskColor(passwordResult.risk_level)}`}>
                            {passwordResult.risk_level.toUpperCase()}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-2">Risk Level</p>
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    <div className={`p-4 rounded-lg ${passwordResult.compromised ? 'bg-red-500/5 border border-red-500/20' : 'bg-[#00FF94]/5 border border-[#00FF94]/20'}`}>
                      <h4 className={`font-medium mb-2 ${passwordResult.compromised ? 'text-red-400' : 'text-[#00FF94]'}`}>
                        {passwordResult.compromised ? 'Action Required' : 'Good News!'}
                      </h4>
                      <ul className="space-y-1">
                        {passwordResult.recommendations?.map((rec, i) => (
                          <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${passwordResult.compromised ? 'text-red-400' : 'text-[#00FF94]'}`} />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Source Attribution */}
                    <p className="text-xs text-gray-600 text-center">
                      Data provided by {passwordResult.source}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Removal Tab */}
          <TabsContent value="removal" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Start Removal */}
              <Card className="bg-[#0a0a0a] border-[#262626]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-[#00FF94]" />
                    Start Data Removal
                  </CardTitle>
                  <CardDescription className="text-gray-500">
                    Remove your info from 100+ data brokers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Email *</label>
                    <Input
                      type="email"
                      value={removalForm.email}
                      onChange={(e) => setRemovalForm({...removalForm, email: e.target.value})}
                      className="bg-[#171717] border-[#262626] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Full Name *</label>
                    <Input
                      value={removalForm.full_name}
                      onChange={(e) => setRemovalForm({...removalForm, full_name: e.target.value})}
                      className="bg-[#171717] border-[#262626] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1 block">Phone (optional)</label>
                    <Input
                      value={removalForm.phone}
                      onChange={(e) => setRemovalForm({...removalForm, phone: e.target.value})}
                      className="bg-[#171717] border-[#262626] text-white"
                    />
                  </div>
                  <Button 
                    onClick={startDataRemoval}
                    disabled={loading}
                    className="w-full btn-primary"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Start Removal Process
                  </Button>
                </CardContent>
              </Card>

              {/* Removal Status */}
              <Card className="bg-[#0a0a0a] border-[#262626]">
                <CardHeader>
                  <CardTitle className="text-white">Removal Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  {removalStatus && removalStatus.length > 0 ? (
                    <div className="space-y-4">
                      {removalStatus.slice(0, 1).map((removal, i) => (
                        <div key={i}>
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-gray-400">Progress</span>
                            <span className="text-white">{removal.brokers_completed}/{removal.brokers_total}</span>
                          </div>
                          <Progress 
                            value={(removal.brokers_completed / removal.brokers_total) * 100} 
                            className="h-3 bg-[#262626]"
                          />
                          <div className="mt-4 max-h-[200px] overflow-y-auto space-y-2">
                            {removal.broker_status?.slice(0, 8).map((broker, j) => (
                              <div key={j} className="flex items-center justify-between p-2 bg-[#171717] rounded">
                                <span className="text-sm text-gray-400">{broker.name}</span>
                                <Badge className={`text-xs ${
                                  broker.status === 'completed' ? 'bg-green-500/10 text-green-400' :
                                  broker.status === 'requested' ? 'bg-yellow-500/10 text-yellow-400' :
                                  'bg-gray-500/10 text-gray-400'
                                }`}>
                                  {broker.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Trash2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No active removal requests</p>
                      <p className="text-xs mt-1">Start a removal to see progress here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Identity Monitor Tab */}
          <TabsContent value="identity" className="space-y-6">
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#00FF94]" />
                  Identity Monitoring Dashboard
                </CardTitle>
                <CardDescription className="text-gray-500">
                  Continuous monitoring for new exposures
                </CardDescription>
              </CardHeader>
              <CardContent>
                {identityStatus ? (
                  <div className="space-y-6">
                    {/* Status */}
                    <div className="flex items-center gap-3 p-4 bg-[#171717] rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" />
                      <span className="text-green-400 font-medium">Monitoring Active</span>
                      <span className="text-gray-500 text-sm ml-auto">
                        Next scan: {identityStatus.next_scan ? new Date(identityStatus.next_scan).toLocaleString() : 'Scheduled'}
                      </span>
                    </div>

                    {/* Scan Results */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-[#171717] rounded-lg text-center">
                        <p className="text-2xl font-mono text-white">{identityStatus.scan_results?.dark_web_mentions || 0}</p>
                        <p className="text-xs text-gray-500">Dark Web Mentions</p>
                      </div>
                      <div className="p-4 bg-[#171717] rounded-lg text-center">
                        <p className="text-2xl font-mono text-white">{identityStatus.scan_results?.data_broker_listings || 0}</p>
                        <p className="text-xs text-gray-500">Broker Listings</p>
                      </div>
                      <div className="p-4 bg-[#171717] rounded-lg text-center">
                        <p className="text-2xl font-mono text-white capitalize">{identityStatus.scan_results?.social_media_exposure || 'low'}</p>
                        <p className="text-xs text-gray-500">Social Exposure</p>
                      </div>
                      <div className="p-4 bg-[#171717] rounded-lg text-center">
                        <p className="text-2xl font-mono text-white">{identityStatus.scan_results?.public_records_found || 0}</p>
                        <p className="text-xs text-gray-500">Public Records</p>
                      </div>
                    </div>

                    {/* Protected Items */}
                    <div className="p-4 bg-[#171717] rounded-lg">
                      <h4 className="font-medium text-white mb-3">Monitored Items</h4>
                      <div className="space-y-2">
                        {identityStatus.protected_items?.emails?.map((email, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-[#00FF94]" />
                            <span className="text-gray-400">{email}</span>
                            <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Opt-Out Guide Tab */}
          <TabsContent value="optout" className="space-y-6" data-testid="optout-guide-tab">
            <Card className="bg-[#0a0a0a] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-[#00FF94]" />
                  Data Broker Opt-Out Guide
                </CardTitle>
                <CardDescription className="text-gray-500">
                  Track your opt-out progress. Check the box after submitting each request - we'll remind you to follow up in 30 days.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Progress Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-[#171717] rounded-lg p-4 text-center border border-[#262626]">
                    <p className="text-2xl font-bold text-[#00FF94]">28</p>
                    <p className="text-xs text-gray-500">Total Brokers</p>
                  </div>
                  <div className="bg-[#171717] rounded-lg p-4 text-center border border-[#00FF94]/30">
                    <p className="text-2xl font-bold text-blue-400">{optoutStats.total_submitted}</p>
                    <p className="text-xs text-gray-500">Submitted</p>
                  </div>
                  <div className="bg-[#171717] rounded-lg p-4 text-center border border-[#262626]">
                    <p className="text-2xl font-bold text-green-400">{optoutStats.total_completed}</p>
                    <p className="text-xs text-gray-500">Confirmed</p>
                  </div>
                  <div className="bg-[#171717] rounded-lg p-4 text-center border border-[#262626]">
                    <p className="text-2xl font-bold text-yellow-400">{optoutStats.pending_reminders}</p>
                    <p className="text-xs text-gray-500">Pending Check</p>
                  </div>
                </div>

                {/* Progress Bar */}
                {optoutStats.total_submitted > 0 && (
                  <div className="bg-[#171717] rounded-lg p-4 border border-[#262626] mb-6">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Your Progress</span>
                      <span className="text-[#00FF94]">{Math.round((optoutStats.total_submitted / 28) * 100)}% submitted</span>
                    </div>
                    <Progress value={(optoutStats.total_submitted / 28) * 100} className="h-2" />
                  </div>
                )}

                {/* Instructions */}
                <div className="bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg p-4 mb-6">
                  <p className="text-sm text-[#00FF94]">
                    <strong>📝 How to use:</strong> Click a broker link to visit their opt-out page. After submitting your request, check the box to track it. We'll set a 30-day reminder to follow up.
                  </p>
                </div>

                {/* People Search Sites */}
                <div className="space-y-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Search className="w-4 h-4 text-[#00FF94]" />
                    People Search Sites
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { name: "Spokeo", url: "https://www.spokeo.com/optout", difficulty: "Easy", time: "3-5 days" },
                      { name: "WhitePages", url: "https://www.whitepages.com/suppression-requests", difficulty: "Easy", time: "24-48 hours" },
                      { name: "BeenVerified", url: "https://www.beenverified.com/f/optout/search", difficulty: "Medium", time: "24 hours" },
                      { name: "Intelius", url: "https://www.intelius.com/opt-out", difficulty: "Easy", time: "7 days" },
                      { name: "TruePeopleSearch", url: "https://www.truepeoplesearch.com/removal", difficulty: "Easy", time: "Instant" },
                      { name: "FastPeopleSearch", url: "https://www.fastpeoplesearch.com/removal", difficulty: "Easy", time: "Instant" },
                      { name: "That'sThem", url: "https://thatsthem.com/optout", difficulty: "Easy", time: "24-72 hours" },
                      { name: "USPhoneBook", url: "https://www.usphonebook.com/opt-out", difficulty: "Easy", time: "Instant" },
                      { name: "Nuwber", url: "https://nuwber.com/removal/link", difficulty: "Medium", time: "24-48 hours" },
                      { name: "Radaris", url: "https://radaris.com/control/privacy", difficulty: "Hard", time: "7-14 days" },
                    ].map((broker, idx) => {
                      const tracked = isOptoutTracked(broker.name);
                      const completed = isOptoutCompleted(broker.name);
                      return (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-3 bg-[#171717] rounded-lg border transition-colors ${
                            completed ? 'border-green-500/30 bg-green-500/5' : 
                            tracked ? 'border-blue-500/30 bg-blue-500/5' : 
                            'border-[#262626] hover:border-[#00FF94]/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleOptoutTracking(broker, 'people_search')}
                              disabled={savingOptout === broker.name}
                              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                completed ? 'bg-green-500 border-green-500' :
                                tracked ? 'bg-blue-500 border-blue-500' : 
                                'border-gray-600 hover:border-[#00FF94]'
                              }`}
                            >
                              {savingOptout === broker.name ? (
                                <Loader2 className="w-3 h-3 text-white animate-spin" />
                              ) : tracked ? (
                                <Check className="w-3 h-3 text-white" />
                              ) : null}
                            </button>
                            <div>
                              <p className={`font-medium text-sm ${completed ? 'text-green-400 line-through' : 'text-white'}`}>{broker.name}</p>
                              <p className="text-xs text-gray-500">{broker.time} • {broker.difficulty}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {tracked && !completed && (
                              <button
                                onClick={() => markOptoutCompleted(broker.name, true)}
                                className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-500/10"
                              >
                                Confirm
                              </button>
                            )}
                            <a
                              href={broker.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-[#00FF94]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Background Check Sites */}
                <div className="space-y-4 pt-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#00FF94]" />
                    Background Check Sites
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { name: "Instant Checkmate", url: "https://www.instantcheckmate.com/opt-out/", difficulty: "Medium", time: "48 hours" },
                      { name: "TruthFinder", url: "https://www.truthfinder.com/opt-out/", difficulty: "Easy", time: "24-48 hours" },
                      { name: "PeopleFinders", url: "https://www.peoplefinders.com/opt-out", difficulty: "Easy", time: "24-48 hours" },
                      { name: "USA People Search", url: "https://www.usa-people-search.com/manage", difficulty: "Easy", time: "24 hours" },
                      { name: "PublicRecordsNow", url: "https://www.publicrecordsnow.com/static/view/optout", difficulty: "Easy", time: "48 hours" },
                      { name: "Cyberbackgroundchecks", url: "https://www.cyberbackgroundchecks.com/removal", difficulty: "Easy", time: "Instant" },
                    ].map((broker, idx) => {
                      const tracked = isOptoutTracked(broker.name);
                      const completed = isOptoutCompleted(broker.name);
                      return (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-3 bg-[#171717] rounded-lg border transition-colors ${
                            completed ? 'border-green-500/30 bg-green-500/5' : 
                            tracked ? 'border-blue-500/30 bg-blue-500/5' : 
                            'border-[#262626] hover:border-[#00FF94]/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleOptoutTracking(broker, 'background_check')}
                              disabled={savingOptout === broker.name}
                              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                completed ? 'bg-green-500 border-green-500' :
                                tracked ? 'bg-blue-500 border-blue-500' : 
                                'border-gray-600 hover:border-[#00FF94]'
                              }`}
                            >
                              {savingOptout === broker.name ? (
                                <Loader2 className="w-3 h-3 text-white animate-spin" />
                              ) : tracked ? (
                                <Check className="w-3 h-3 text-white" />
                              ) : null}
                            </button>
                            <div>
                              <p className={`font-medium text-sm ${completed ? 'text-green-400 line-through' : 'text-white'}`}>{broker.name}</p>
                              <p className="text-xs text-gray-500">{broker.time} • {broker.difficulty}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {tracked && !completed && (
                              <button
                                onClick={() => markOptoutCompleted(broker.name, true)}
                                className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-500/10"
                              >
                                Confirm
                              </button>
                            )}
                            <a
                              href={broker.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-[#00FF94]"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Marketing Data Brokers */}
                <div className="space-y-4 pt-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#00FF94]" />
                    Marketing & Data Aggregators
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { name: "Acxiom", url: "https://isapps.acxiom.com/optout/optout.aspx", difficulty: "Medium", time: "2-4 weeks" },
                      { name: "Oracle (Datalogix)", url: "https://www.oracle.com/legal/privacy/marketing-cloud-data-cloud-privacy-policy.html#optout", difficulty: "Hard", time: "4-6 weeks" },
                      { name: "Epsilon", url: "https://www.epsilon.com/us/privacy-policy", difficulty: "Medium", time: "2-4 weeks" },
                      { name: "LexisNexis", url: "https://optout.lexisnexis.com/", difficulty: "Hard", time: "2-4 weeks" },
                      { name: "Experian", url: "https://www.experian.com/privacy/opting_out", difficulty: "Medium", time: "2-4 weeks" },
                      { name: "Equifax", url: "https://www.equifax.com/personal/credit-report-services/opt-out/", difficulty: "Medium", time: "5 days" },
                    ].map((broker, idx) => {
                      const tracked = isOptoutTracked(broker.name);
                      const completed = isOptoutCompleted(broker.name);
                      return (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-3 bg-[#171717] rounded-lg border transition-colors ${
                            completed ? 'border-green-500/30 bg-green-500/5' : 
                            tracked ? 'border-blue-500/30 bg-blue-500/5' : 
                            'border-[#262626] hover:border-[#00FF94]/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleOptoutTracking(broker, 'marketing')}
                              disabled={savingOptout === broker.name}
                              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                completed ? 'bg-green-500 border-green-500' :
                                tracked ? 'bg-blue-500 border-blue-500' : 
                                'border-gray-600 hover:border-[#00FF94]'
                              }`}
                            >
                              {savingOptout === broker.name ? (
                                <Loader2 className="w-3 h-3 text-white animate-spin" />
                              ) : tracked ? (
                                <Check className="w-3 h-3 text-white" />
                              ) : null}
                            </button>
                            <div>
                              <p className={`font-medium text-sm ${completed ? 'text-green-400 line-through' : 'text-white'}`}>{broker.name}</p>
                              <p className="text-xs text-gray-500">{broker.time} • {broker.difficulty}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {tracked && !completed && (
                              <button
                                onClick={() => markOptoutCompleted(broker.name, true)}
                                className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-500/10"
                              >
                                Confirm
                              </button>
                            )}
                            <a
                              href={broker.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-600 hover:text-[#00FF94]"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-4 pt-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#00FF94]" />
                    Additional Privacy Actions
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      { name: "Google Activity Controls", url: "https://myactivity.google.com/activitycontrols", desc: "Manage Google tracking" },
                      { name: "Facebook Off-Facebook Activity", url: "https://www.facebook.com/off_facebook_activity", desc: "Control FB tracking" },
                      { name: "Direct Marketing Association", url: "https://www.dmachoice.org/", desc: "Opt out of junk mail" },
                      { name: "National Do Not Call Registry", url: "https://www.donotcall.gov/", desc: "Stop telemarketer calls" },
                      { name: "OptOutPrescreen", url: "https://www.optoutprescreen.com/", desc: "Stop credit card offers" },
                      { name: "Catalog Choice", url: "https://www.catalogchoice.org/", desc: "Stop catalog mailings" },
                    ].map((action, idx) => {
                      const tracked = isOptoutTracked(action.name);
                      const completed = isOptoutCompleted(action.name);
                      return (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                            completed ? 'border-green-500/30 bg-green-500/5' : 
                            tracked ? 'border-blue-500/30 bg-blue-500/5' : 
                            'bg-[#00FF94]/5 border-[#00FF94]/20 hover:bg-[#00FF94]/10'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleOptoutTracking({ name: action.name, url: action.url }, 'privacy_action')}
                              disabled={savingOptout === action.name}
                              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                completed ? 'bg-green-500 border-green-500' :
                                tracked ? 'bg-blue-500 border-blue-500' : 
                                'border-[#00FF94]/50 hover:border-[#00FF94]'
                              }`}
                            >
                              {savingOptout === action.name ? (
                                <Loader2 className="w-3 h-3 text-white animate-spin" />
                              ) : tracked ? (
                                <Check className="w-3 h-3 text-white" />
                              ) : null}
                            </button>
                            <div>
                              <p className={`font-medium text-sm ${completed ? 'text-green-400 line-through' : 'text-white'}`}>{action.name}</p>
                              <p className="text-xs text-gray-500">{action.desc}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {tracked && !completed && (
                              <button
                                onClick={() => markOptoutCompleted(action.name, true)}
                                className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded bg-blue-500/10"
                              >
                                Done
                              </button>
                            )}
                            <a
                              href={action.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#00FF94]"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Step by Step Guide */}
                <div className="bg-[#171717] rounded-lg p-6 border border-[#262626] mt-6">
                  <h3 className="text-white font-semibold mb-4">📋 How to Opt-Out (General Steps)</h3>
                  <ol className="space-y-3 text-sm text-gray-400">
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <span>Click the link for each data broker above</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <span>Search for your name and find your profile listing</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <span>Follow the opt-out/removal process (may require email verification)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                      <span>Keep a record of your removal requests with dates</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#00FF94]/20 text-[#00FF94] flex items-center justify-center flex-shrink-0 text-xs font-bold">5</span>
                      <span>Set a calendar reminder to re-check in 30-60 days</span>
                    </li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
